import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import {
  CommentFormModel,
  CommentsData,
  ComplaintComment,
} from '../../../models/CommentsData';
import { TranslateService } from '@ngx-translate/core';
import { ComplaintsTicktsService } from '../../../services/complaintsTickts.service';
import Swal from 'sweetalert2';
import { Subscription } from 'rxjs';
import { SignalEventType } from 'projects/shared/src/models/SignalModel';
import { SignalRService } from 'projects/shared/src/services/signal-rservice.service';

@Component({
  selector: 'app-complaint-details-comments',
  standalone: false,

  templateUrl: './complaint-details-comments.component.html',
})
export class ComplaintDetailsCommentsComponent {
  loading = false;
  @Input({ required: true }) recordId!: number;
  commentsLoading = false;
  comments: CommentsData = new CommentsData();
  pageNumber = 1;
  selectedComment!: ComplaintComment | null;
  activeAddedReplay = 0;

  // Properties for show more/less functionality
  expandedComments: Set<number> = new Set();
  initialRepliesToShow = 1;
  private subscriptions: Subscription[] = [];

  constructor(
    private service: ComplaintsTicktsService,
    private translate: TranslateService,
    private el: ElementRef,
    private signal: SignalRService
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.signal
        .getDataStream<string>(SignalEventType.commentsticketUpdate)
        .subscribe((message) => {
          this.updateComments(message.data);
        })
    );
    this.getComments();
  }

  updateComments(data: any) {
    if (this.recordId == data.id) {
      this.getComments();
    }
  }

  getComments(event = false) {
    this.commentsLoading = true;
    if (event) {
      this.pageNumber = this.pageNumber + 1;
    } else {
      this.pageNumber = 1;
    }
    let model = {
      complaintId: this.recordId,
      pageNumber: this.pageNumber,
      pageSize: 2,
    };
    this.service.getComments(model).subscribe((data: any) => {
      if (
        this.comments &&
        this.comments.data &&
        this.comments.data.length &&
        event
      ) {
        this.comments.data = [...this.comments.data, ...data.data];
        this.comments.totalCount = data.totalCount;
      } else {
        this.comments = data;
      }
      this.commentsLoading = false;
    }, (err: any) => {
      this.commentsLoading = false;
    });
  }

  isButtonActive() {
    return this.comments.totalCount == this.comments.data.length;
  }

  deleteComment(comment: ComplaintComment) {
    Swal.fire({
      title: this.translate.instant('swalMsg.Areyousuretodelete'),
      text: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('swalMsg.Yesdeleteit'),
      cancelButtonText: this.translate.instant('swalMsg.Nokeepit'),
    }).then((result) => {
      if (result.value) {
        this.commentsLoading = true;
        this.service.removeComment(comment.id).subscribe(
          (data: any) => {
            if (data) {
              Swal.fire({
                title: this.translate.instant('swalMsg.Success'),
                text: this.translate.instant('swalMsg.deletededsuccessfuly'),
                imageUrl: './assets/images/icons/success.svg',
                confirmButtonText: this.translate.instant('swalMsg.Ok'),
              });
              this.getComments();
            }
          },
          (error: any) => { }
        );
      }
    });
  }

  addReplay(commentId: number) {
    this.activeAddedReplay = commentId;
    this.selectedComment = null;
    setTimeout(() => {
      const formReplay: HTMLElement = this.el.nativeElement.querySelector(
        '#form-replay' + commentId
      );
      formReplay.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      formReplay.querySelector('textarea')?.focus();
    }, 10);
  }

  submitComment(formValue: any , isReplay = true) {
    let commentFormModel: CommentFormModel = new CommentFormModel();
    commentFormModel.ticketId = this.recordId;
    commentFormModel.comment = formValue['comment'];
    if (this.selectedComment) {
      commentFormModel.commentId = this.selectedComment.id;
    }
    if (this.activeAddedReplay && isReplay) {
      commentFormModel.parentCommentId = this.activeAddedReplay;
    }
    if (!this.activeAddedReplay) {
      commentFormModel.sendTo = [formValue['sendCommentTo']];
    }

    if (commentFormModel.commentId) {
      this.service.editComment(commentFormModel).subscribe(
        (res: any) => {
          this.loading = false;
          Swal.fire({
            title: this.translate.instant('swalMsg.updatedsuccessfully'),
            imageUrl: './assets/images/icons/success.svg',
            confirmButtonText: this.translate.instant('swalMsg.Ok'),
          });
          this.selectedComment = null
          this.activeAddedReplay = 0
          this.getComments();
        },
        (error: any) => {
          this.loading = false;
        }
      );
    } else {
      this.service.addComment(commentFormModel).subscribe(
        (res: any) => {
          this.loading = false;
          Swal.fire({
            title: this.translate.instant('swalMsg.savedsuccessfully'),
            imageUrl: './assets/images/icons/success.svg',
            confirmButtonText: this.translate.instant('swalMsg.Ok'),
          });
          this.selectedComment = null
          this.activeAddedReplay = 0
          this.getComments();
        },
        (error: any) => {
          this.loading = false;
        }
      );
    }
  }

  // Method to toggle replies visibility
  toggleReplies(commentId: number) {
    if (this.expandedComments.has(commentId)) {
      this.expandedComments.delete(commentId);
    } else {
      this.expandedComments.add(commentId);
    }
  }

  // Method to check if replies are expanded
  isRepliesExpanded(commentId: number): boolean {
    return this.expandedComments.has(commentId);
  }

  // Method to get visible replies
  getVisibleReplies(replies: ComplaintComment[]): ComplaintComment[] {
    return replies.slice(0, this.initialRepliesToShow);
  }

  // Method to check if there are more replies to show
  hasMoreReplies(replies: ComplaintComment[]): boolean {
    return replies.length > this.initialRepliesToShow;
  }
}
